import { useState, useEffect, useRef } from "react";
import { useBookCta } from "@/hooks/use-book-href";
import { Link, useLocation } from "wouter";
import { Menu, X, ChevronDown, Search } from "lucide-react";
import { SiteSearch } from "@/components/layout/site-search";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import BrandLockup from "@/components/layout/brand-lockup";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { KUALOA_PROPERTY_SLUG, shouldShowPortfolioNav } from "@shared/property-context";

export function Header() {
  const flags = useFeatureFlags();
  const bookCta = useBookCta();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  // Publish the live header height to --app-header-h so forms can scroll their
  // active step to sit just below the fixed header (never hidden behind it).
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const publish = () =>
      document.documentElement.style.setProperty("--app-header-h", `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener("orientationchange", publish);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", publish);
    };
  }, []);

  // Only media-backed heroes use the transparent state. Re-evaluate when
  // route data arrives, as property heroes render after their query resolves.
  useEffect(() => {
    const update = () => setIsScrolled(!document.querySelector('[data-hero-header]') || window.scrollY > 80);
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {childList:true, subtree:true});
    window.addEventListener('scroll', update, {passive:true});
    setIsMenuOpen(false);
    return () => { observer.disconnect(); window.removeEventListener('scroll', update); };
  }, [location]);

  const showPortfolioNav = shouldShowPortfolioNav(flags.publishedHomeCount);
  const stayHref = flags.propertyRoutes ? `/properties/${KUALOA_PROPERTY_SLUG}` : "/stay";
  const isPortfolioBrand = flags.portfolioHomepage && showPortfolioNav;
  const navigation = [
    { name: "Home", href: "/" },
    ...(showPortfolioNav ? [{ name: "Our Homes", href: "/properties" }] : [{ name: "Stay", href: stayHref }]),
    { name: "Experiences", href: "/experiences" },
    { name: "Travel Guide", href: "/blog" },
    { name: "Contact", href: "/contact" },
  ];

  const planYourTripLinks = [
    { name: "FAQ", href: "/faq" },
    { name: "Guest Reviews", href: "/reviews" },
    { name: "About", href: "/about" },
    { name: "Ka'a'awa Guide", href: "/kaaawa" },
    { name: "Policies", href: "/policies" },
  ];

  const isActive = (href: string) => {
    if (href === "/" && location === "/") return true;
    if (href !== "/" && location.startsWith(href)) return true;
    return false;
  };

  const isPlanYourTripActive = planYourTripLinks.some((item) => isActive(item.href));

  const navLinkClass = (active: boolean) =>
    `text-luxury-sm font-serif font-[500] tracking-luxury-tight uppercase whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded px-2 xl:px-3 py-2 ${
      isScrolled
        ? `transition-colors hover:text-ink/70 ${active ? "text-ink underline underline-offset-8 decoration-sun decoration-2" : "text-foreground"}`
        : `wordmark-gold transition-colors ${active ? "scale-105 font-[500]" : ""}`
    }`;

  // Handle escape key and focus management for mobile menu
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isMenuOpen) return;

      if (e.key === 'Escape') {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      // Focus trap implementation for Tab/Shift+Tab
      if (e.key === 'Tab') {
        const focusableElements = mobileMenuRef.current?.querySelectorAll(
          'a, button, [tabindex]:not([tabindex="-1"])'
        );
        
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        // If Shift+Tab and we're on the first element, go to last
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
        // If Tab and we're on the last element, go to first
        else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      
      // Focus first focusable element when menu opens
      const firstFocusable = mobileMenuRef.current?.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable instanceof HTMLElement) {
        firstFocusable.focus();
      }

      // Prevent scrolling on background when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scrolling when menu is closed
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = ''; // Cleanup on unmount
    };
  }, [isMenuOpen]);

  return (
    <>
      {/* Skip links removed from the guest-facing site at the owner's request.
         They are kept in the admin panel (see AdminLayout), where the sidebar is
         long enough for them to earn their place and no guest ever sees them. */}
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 header-transition ${
          isMenuOpen ? 'z-50' : 'z-40'
        } ${
          isScrolled 
            ? 'header-solid' 
            : 'header-transparent'
        }`}
        role="banner"
      >
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          {/* Logo */}
          <Link 
            href="/" 
            className="flex items-center space-x-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-lg" 
            data-testid="logo-link"
            aria-label={isPortfolioBrand ? "Vacation Rental Oahu - Return to homepage" : "The Kualoa Beach House - Return to homepage"}
          >
            {/* On the homepage the mark appears once the header turns solid on
                scroll; over the hero video the header carries no logo. Other
                pages count as scrolled from the start. */}
            <span
              className={`inline-flex transition-opacity duration-300 ${isScrolled ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              aria-hidden={!isScrolled}
            >
              <BrandLockup variant={isScrolled ? "ink" : "light"} size="sm" showLocation={false} brand={isPortfolioBrand ? "portfolio" : "kualoa"} />
            </span>
          </Link>

          {/* Desktop Navigation: gated at lg. Below 1024px the full lockup,
              six nav items and CTA cannot fit on one line without wrapping,
              so tablets get the mobile menu instead. */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-8">
            <nav
              className="flex items-center gap-0.5 xl:gap-3"
              role="navigation"
              aria-label="Main navigation"
            >
            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={navLinkClass(isActive(item.href))}
                data-testid={`nav-${item.name.toLowerCase()}`}
                aria-current={isActive(item.href) ? 'page' : undefined}
              >
                {item.name}
              </Link>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`inline-flex items-center gap-1 ${navLinkClass(isPlanYourTripActive)}`}
                data-testid="nav-plan-your-trip"
                aria-current={isPlanYourTripActive ? 'page' : undefined}
              >
                Plan Your Trip
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[10rem]">
                {planYourTripLinks.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link
                      href={item.href}
                      data-testid={`nav-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            </nav>
            
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className={`inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood ${isScrolled ? "text-foreground hover:bg-muted" : "hero-nav-color hover:bg-transparent"}`}
              aria-label="Search the site"
              data-testid="site-search-open"
            >
              <Search className="h-4 w-4" aria-hidden="true" />
            </button>

            {/* Book Now CTA: books the home on screen, or sends the visitor to choose one */}
            <Link href={bookCta.href}>
              <Button 
                size="lg"
                variant="bronze"
                className={`${isScrolled ? 'btn-bronze-enhanced' : 'btn-white-overlay'} font-serif font-[500] tracking-luxury-tight touch-target-optimal`}
                data-testid="book-now-cta"
              >
                {bookCta.label}
              </Button>
            </Link>
            
            {user && (
              <div className="flex items-center space-x-3">
                <Link
                  href="/admin"
                  className={`text-luxury-xs font-serif font-[500] tracking-luxury-tight uppercase hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded px-2 py-1 ${
                    isScrolled ? "text-muted-foreground" : "text-white/80"
                  }`}
                  data-testid="admin-link"
                >
                  Admin
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => logoutMutation.mutate()}
                  disabled={logoutMutation.isPending}
                  data-testid="logout-button"
                >
                  Logout
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            ref={menuButtonRef}
            variant="ghost"
            size="sm"
            className={`lg:hidden touch-target focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
              isScrolled ? "text-foreground" : "hero-nav-color"
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            data-testid="mobile-menu-button"
          >
            {isMenuOpen ? <X className={`h-5 w-5 ${isScrolled ? "text-foreground" : "hero-nav-color"}`} aria-hidden="true" /> : <Menu className={`h-5 w-5 ${isScrolled ? "text-foreground" : "hero-nav-color"}`} aria-hidden="true" />}
          </Button>
        </div>
        
            <SiteSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
      
      {/* Full Page Mobile Menu Overlay */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 z-[100] lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          {/* Background Overlay */}
          <div
            className="absolute inset-0 bg-shell"
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Menu Content */}
          <nav 
            ref={mobileMenuRef}
            id="mobile-navigation"
            className="relative h-full flex flex-col bg-shell"
            role="navigation"
            aria-label="Mobile navigation"
          >
            {/* Header with Logo and Close Button */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <Link 
                href="/" 
                onClick={() => setIsMenuOpen(false)}
                className="focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood focus-visible:ring-offset-2 rounded-lg"
                aria-label={isPortfolioBrand ? "Vacation Rental Oahu - Return to homepage" : "The Kualoa Beach House - Return to homepage"}
              >
                <BrandLockup variant="ink" size="sm" showLocation={false} brand={isPortfolioBrand ? "portfolio" : "kualoa"} />
              </Link>
              
              <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 rounded-full border border-border text-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood focus-visible:ring-offset-2"
                onClick={() => { setIsMenuOpen(false); setSearchOpen(true); }}
                aria-label="Search the site"
                data-testid="mobile-search-open"
              >
                <Search className="h-5 w-5" aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 rounded-full border border-border text-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood focus-visible:ring-offset-2"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Close navigation menu"
                data-testid="mobile-menu-close"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </Button>
              </div>
            </div>
            
            {/* Main Menu Content */}
            <div className="flex-1 flex flex-col px-7 pt-8 overflow-y-auto" style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
              {/* Primary Navigation */}
              <div>
                <p
                  className="menu-item-enter text-[11px] font-sans font-normal uppercase tracking-[0.3em] text-driftwood mb-4"
                  style={{ animationDelay: '60ms' }}
                >
                  Explore
                </p>
                <div className="flex flex-col">
                  {navigation.map((item, i) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      className="menu-item-enter group flex items-baseline justify-between py-3 min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
                      style={{ animationDelay: `${110 + i * 55}ms` }}
                      onClick={() => setIsMenuOpen(false)}
                      data-testid={`mobile-nav-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      aria-current={isActive(item.href) ? 'page' : undefined}
                    >
                      <span
                        className={`font-serif text-[1.75rem] leading-tight text-ink transition-colors duration-200 ${
                          isActive(item.href)
                            ? "font-[500] underline underline-offset-[10px] decoration-sun decoration-[3px]"
                            : "font-[500] group-hover:text-ink-soft"
                        }`}
                      >
                        {item.name}
                      </span>
                      <span className="font-sans text-[10px] tracking-[0.25em] text-driftwood/70 tabular-nums" aria-hidden="true">
                        0{i + 1}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Secondary: Plan Your Trip */}
              <div className="mt-7 pt-6 border-t border-sand-deep/70">
                <p
                  className="menu-item-enter text-[11px] font-sans font-normal uppercase tracking-[0.3em] text-driftwood mb-3"
                  style={{ animationDelay: '400ms' }}
                >
                  Plan Your Trip
                </p>
                <div className="grid grid-cols-2 gap-x-4">
                  {planYourTripLinks.map((item, i) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      className={`menu-item-enter block min-h-[44px] py-2.5 font-serif text-luxury-lg transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded ${
                        isActive(item.href)
                          ? "text-ink font-[500] underline underline-offset-8 decoration-sun decoration-2"
                          : "text-ink-soft hover:text-ink font-[500]"
                      }`}
                      style={{ animationDelay: `${440 + i * 45}ms` }}
                      onClick={() => setIsMenuOpen(false)}
                      data-testid={`mobile-nav-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      aria-current={isActive(item.href) ? 'page' : undefined}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Admin Section */}
              {user && (
                <div className="mt-6 pt-5 border-t border-sand-deep/70 flex items-center justify-between">
                  <Link
                    href="/admin"
                    className="text-luxury-sm font-sans tracking-luxury-tight text-ink-soft hover:text-ink transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded py-2 min-h-[44px] inline-flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                    data-testid="mobile-admin-link"
                  >
                    Admin Dashboard
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      logoutMutation.mutate();
                      setIsMenuOpen(false);
                    }}
                    disabled={logoutMutation.isPending}
                    className="text-ink-soft hover:text-ink min-h-[44px]"
                    data-testid="mobile-logout-button"
                  >
                    Logout
                  </Button>
                </div>
              )}

              {/* Book Now CTA + quiet footer */}
              <div className="mt-auto pt-8">
                <Link
                  href={bookCta.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="menu-item-enter block"
                  style={{ animationDelay: '520ms' }}
                >
                  <Button
                    size="lg"
                    className="w-full bg-sun text-shell hover:bg-sun-hover font-sans font-semibold uppercase tracking-[0.18em] text-luxury-sm py-4 h-auto rounded-full shadow-sm touch-target-optimal"
                    data-testid="mobile-book-now-cta"
                  >
                    {bookCta.label}
                  </Button>
                </Link>
                <div
                  className="menu-item-enter mt-6 flex items-center justify-between text-[10px] font-sans font-normal uppercase tracking-[0.25em] text-driftwood"
                  style={{ animationDelay: '580ms' }}
                >
                  <span>Ka'a'awa, Oahu</span>
                  <Link
                    href="/contact"
                    onClick={() => setIsMenuOpen(false)}
                    className="inline-flex items-center min-h-[44px] -my-3 underline underline-offset-4 decoration-sand-deep hover:text-ink transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-driftwood focus-visible:ring-offset-2 rounded"
                    data-testid="mobile-footer-contact"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
